import { useState } from 'react';
import { BookmarkIcon, BotIcon, DatabaseIcon, FileKeyIcon, FileTextIcon, HashIcon, Link2Icon, MessageSquareIcon, PlusIcon, SparklesIcon, UserIcon, WandSparklesIcon } from 'lucide-react';
import { activityFor, attentionCountFor, chatsFor, linkedFor, statePlayFor, type ActivityEntry, type ActivityTag, type LinkedCategory } from '../../data/trackDetail';
import { avatarHue, type Hue } from '../alt/hues';
import { EmptyState, Glyph, MetaStat, Panel, Row, RowText, SearchField, SelectPill, SURFACE } from '../dashboard/ui';

/**
 * The three non-chat track views. Every neutral here is a semantic --app-*
 * token (see components/dashboard/ui.tsx): near-white canvas, white
 * surfaces, neutral hairlines. Colour appears only where it carries meaning
 * — the group's own hue, and the event-type legend dots.
 */

export type TrackDetailViewProps = {
  trackName: string;
  group: string;
  groupHue: Hue;
};

// Legend-style dot + label rather than a filled badge: a row of solid pills
// read as busy the moment more than one tag applied. These are the shared
// status tokens, so "issue" is the same red everywhere in the product.
const TAG_DOT: Record<ActivityTag, string> = {
  Discussion: 'var(--app-info)',
  Decision: 'var(--app-success)',
  Issue: 'var(--app-danger)',
  Progress: '#6D4CBF',
  'Customer signal': 'var(--app-warning)',
  Metric: '#4159A6'
};
const CATEGORY_ICON: Record<string, typeof BookmarkIcon> = {
  bookmarks: BookmarkIcon,
  prompts: WandSparklesIcon,
  skills: SparklesIcon,
  credentials: FileKeyIcon,
  databases: DatabaseIcon,
  slack: HashIcon,
  chats: MessageSquareIcon,
  context: FileTextIcon
};
export function TrackOverviewView({
  trackName,
  group,
  groupHue
}: TrackDetailViewProps) {
  const activity = activityFor(trackName);
  const recent = activity.flatMap(day => day.entries).slice(0, 5);
  const chats = chatsFor(trackName);
  const linked = linkedFor(trackName);
  const allLinked = [...linked.left, ...linked.right];
  const context = allLinked.find(category => category.id === 'context')?.items ?? [];
  const primaryChannel = allLinked.find(category => category.id === 'slack')?.items[0];
  return <div className="mx-auto grid w-full max-w-[1040px] grid-cols-1 gap-x-6 gap-y-6 xl:grid-cols-12" data-id="element-3856" data-mp="39td6s029tm6y">
      <section className="xl:col-span-7" data-id="element-3857" data-mp="3espqibxuid5b">
        {/* ONE headline. A card title plus a narrative line beneath it read as
         two stacked headlines competing for the same job. */}
        <div className={`${SURFACE} p-5`} data-id="element-3858" data-mp="3ok2kpa749fei">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-3859" data-mp="3sleb3hxte4nr">
            <h2 className="text-[15px] font-semibold text-app" data-id="element-3860" data-mp="1fmnap7if444">State of play</h2>
            <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold" style={{
            backgroundColor: groupHue.tint,
            color: groupHue.ink
          }} data-id="element-3861" data-mp="1djmda4tjqkm">
              <span className="size-1.5 rounded-full" style={{
              backgroundColor: groupHue.solid
            }} data-id="element-3862" data-mp="238kaa4elv2h5" />
              {group}
            </span>
          </div>
          <p className="mt-2.5 max-w-[66ch] text-[13.5px] leading-relaxed text-app-muted" data-id="element-3863" data-mp="20mb9asqf8imd">{statePlayFor(trackName)}</p>
          <dl className="mt-4 grid grid-cols-3 gap-4 border-t border-app-divider pt-4" data-id="element-3864" data-mp="1vvkz9271vrlc">
            <MetaStat label="Active chats" value={chats.length} data-id="element-3865" data-mp="1qw8gotn7p6ok" />
            <MetaStat label="Recent events" value={recent.length} data-id="element-3866" data-mp="1quf6539ha2ba" />
            <MetaStat label="Needs review" value={attentionCountFor(trackName)} data-id="element-3867" data-mp="1lz89gfemlxvv" />
          </dl>
        </div>

        <h3 className="mb-2.5 mt-6 text-[12.5px] font-semibold text-app-muted" data-id="element-3868" data-mp="2jknrt53834um">Observations</h3>
        <Panel data-id="element-3869" data-mp="2en43fcct9boy">
          {recent.map((entry, index) => <Row key={`${entry.time}-${index}`} data-id="element-3870" data-mp="29mzovrvgqr1d" data-mpi={index}>
              <Glyph icon={entry.author === 'bot' ? BotIcon : UserIcon} data-id="element-3871" data-mp="29leqfr612ny8" />
              <RowText title={entry.text} meta={entry.time} data-id="element-3872" data-mp="24n4wfjw1hte7" />
              <TagLegend tag={entry.tags[0]} data-id="element-3873" data-mp="y82rnup4kvix" />
            </Row>)}
        </Panel>
      </section>

      <section className="xl:col-span-5" data-id="element-3874" data-mp="t8zpqj4fz5f6">
        <h3 className="mb-2.5 text-[12.5px] font-semibold text-app-muted" data-id="element-3875" data-mp="t7yfggv92myi">Chats</h3>
        <Panel data-id="element-3876" data-mp="2i2x82e2dqnjw">
          {chats.map(chat => <Row key={chat.ref} data-id="element-3877" data-mp="2i1vxeapa032p">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full text-[10.5px] font-bold text-white" style={{
            backgroundColor: avatarHue(chat.initials)
          }} data-id="element-3878" data-mp="2mtx6jnr5ddoi">{chat.initials}</span>
              <RowText title={chat.name} meta={chat.when} data-id="element-3879" data-mp="2d3ufht3l69st" />
              <span className="shrink-0 font-mono text-[10.5px] text-app-subtle" data-id="element-3880" data-mp="2d4dmierp7ds2">{chat.ref}</span>
            </Row>)}
        </Panel>

        <h3 className="mb-2.5 mt-6 text-[12.5px] font-semibold text-app-muted" data-id="element-3881" data-mp="35srjcr71w421">Linked</h3>
        <Panel data-id="element-3882" data-mp="35qf4imwla9t7">
          {context.map(item => <Row key={item.title} data-id="element-3883" data-mp="2qwa8s26oqtgj">
              <Glyph icon={FileTextIcon} data-id="element-3884" data-mp="2qv8y3ytl08zc" />
              <RowText title={item.title} meta={item.meta} data-id="element-3885" data-mp="2vubukuvhx6vp" />
            </Row>)}
          {primaryChannel && <Row data-id="element-3886" data-mp="1fglndf92y2hu">
              <Glyph icon={HashIcon} data-id="element-3887" data-mp="1fg2f6q98esh0" />
              <RowText title={primaryChannel.title} meta={primaryChannel.meta} data-id="element-3888" data-mp="1ff0qvk37g9hc" />
            </Row>}
        </Panel>
      </section>
    </div>;
}
export function TrackLinkedView({
  trackName
}: TrackDetailViewProps) {
  const inventory = linkedFor(trackName);
  return <div className="mx-auto grid w-full max-w-[1040px] grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-3889" data-mp="3towbbnus49mt">
      <Panel data-id="element-3890" data-mp="bj01t8tbtk7f">{inventory.left.map(category => <LinkedCategoryRows key={category.id} category={category} data-id="element-3891" data-mp="bhyfg86v1bae" />)}</Panel>
      <Panel data-id="element-3892" data-mp="7hy1sprl544e">{inventory.right.map(category => <LinkedCategoryRows key={category.id} category={category} data-id="element-3893" data-mp="7esbx8b4ty7m" />)}</Panel>
    </div>;
}
export function TrackActivityView({
  trackName
}: TrackDetailViewProps) {
  const days = activityFor(trackName);
  const [query, setQuery] = useState('');
  const [type, setType] = useState<'All types' | ActivityTag>('All types');
  const [authors, setAuthors] = useState<Set<ActivityEntry['author']>>(() => new Set(['human', 'bot']));
  const q = query.trim().toLowerCase();
  const toggleAuthor = (author: ActivityEntry['author']) => setAuthors(current => {
    const next = new Set(current);
    if (next.has(author) && next.size > 1) next.delete(author);else next.add(author);
    return next;
  });
  const matches = (entry: ActivityEntry) => authors.has(entry.author) && (type === 'All types' || entry.tags.includes(type)) && (!q || entry.text.toLowerCase().includes(q));
  return <div className="mx-auto w-full max-w-[1040px]" data-id="element-3894" data-mp="14xlz5lgm84cy">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3" data-id="element-3895" data-mp="19vdz92reyws8">
        <div className="w-full sm:w-[260px]" data-id="element-3896" data-mp="19xgsrwrrz81p">
          <SearchField value={query} onChange={setQuery} placeholder="Search this track" label="Search activity" data-id="element-3897" data-mp="c8f3qvcgjuvx" />
        </div>
        <div className="flex items-center gap-2" data-id="element-3898" data-mp="h78eig0d17se">
          <SelectPill value={type} onChange={value => setType(value as typeof type)} options={['All types', 'Discussion', 'Decision', 'Issue', 'Progress', 'Customer signal', 'Metric']} label="Filter by event type" data-id="element-3899" data-mp="m62vrdioioae" />
          <div className="inline-flex items-center gap-0.5 rounded-lg border border-app bg-app-surface p-1" data-id="element-3900" data-mp="1xlriq4vxnnb7">
            {([['human', 'Human', UserIcon], ['bot', 'Bot', BotIcon]] as const).map(([key, label, Icon]) => <button key={key} type="button" onClick={() => toggleAuthor(key)} aria-pressed={authors.has(key)} className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] font-medium transition-colors duration-200 ease-out ${authors.has(key) ? 'bg-app-text text-white' : 'text-app-subtle hover:text-app'}`} data-id="element-3901" data-mp="3n128qam3qk52">
                <Icon size={12} data-id="element-3902" data-mp="38co7230f91q3" />
                {label}
              </button>)}
          </div>
        </div>
      </div>

      {days.map(day => {
      const entries = day.entries.filter(matches);
      if (!entries.length) return null;
      return <div key={day.date} className="mb-6 last:mb-0" data-id="element-3903" data-mp="2ud6fjfvqrad1">
            <h3 className="mb-2 text-[12px] font-semibold text-app-muted" data-id="element-3904" data-mp="2udokr20dsyx3">{day.date}</h3>
            <Panel data-id="element-3905" data-mp="2pfwhjbuadqdl">
              {entries.map((entry, index) => <Row key={`${day.date}-${entry.time}-${index}`} data-id="element-3906" data-mp="slta8ppv30gg" data-mpi={index}>
                  <Glyph icon={entry.author === 'bot' ? BotIcon : UserIcon} data-id="element-3907" data-mp="sjz1e9rv6ysq" />
                  <span className="shrink-0 font-mono text-[11px] text-app-subtle" data-id="element-3908" data-mp="nni1yjf7sy0c">{entry.time}</span>
                  <RowText title={entry.text} meta={entry.author === 'bot' ? 'Giga' : 'Team member'} data-id="element-3909" data-mp="3snio9yebhw71" />
                  <span className="hidden shrink-0 items-center gap-2.5 sm:flex" data-id="element-3910" data-mp="1g66bh5m1eq2">
                    {entry.tags.map(tag => <TagLegend key={tag} tag={tag} data-id="element-3911" data-mp="3iwxgplgo1hbz" />)}
                  </span>
                </Row>)}
            </Panel>
          </div>;
    })}
      {days.every(day => !day.entries.some(matches)) && <EmptyState data-id="element-3912" data-mp="39uxyiptx077g">Nothing here matches those filters.</EmptyState>}
    </div>;
}
function LinkedCategoryRows({
  category
}: {
  category: LinkedCategory;
}) {
  const Icon = CATEGORY_ICON[category.id] ?? Link2Icon;
  return <>
      <div className="flex items-center justify-between border-b border-app-divider bg-app-subtle px-4 py-2" data-id="element-3913" data-mp="300fol2xwnff7">
        <p className="text-[11.5px] font-semibold text-app-muted" data-id="element-3914" data-mp="y6ymsfwgh8sd">
          {category.name} · {category.items.length}
        </p>
        <button type="button" aria-label={`Add ${category.name}`} className="flex size-6 items-center justify-center rounded-md text-app-subtle transition-colors duration-200 ease-out hover:bg-app-accent-tint hover:text-app-accent" data-id="element-3915" data-mp="136b7ov3ro5sb">
          <PlusIcon size={13} data-id="element-3916" data-mp="2iqlixsx76oae" />
        </button>
      </div>
      {category.items.length ? category.items.map((item, index) => <Row key={`${item.title}-${index}`} data-id="element-3917" data-mp="2xfzt338ll9qu" data-mpi={index}>
            <Glyph icon={Icon} data-id="element-3918" data-mp="2smx3f66xykfx" />
            <RowText title={item.title} meta={item.meta} data-id="element-3919" data-mp="2slvnooezd9rv" />
            {item.badges?.map(badge => <span key={badge} className="shrink-0 rounded-md border border-app px-2 py-0.5 text-[10.5px] font-medium text-app-muted" data-id="element-3920" data-mp="36dabd8ju1z59">{badge}</span>)}
          </Row>) : <p className="border-b border-app-divider px-4 py-3.5 text-[12.5px] text-app-subtle last:border-0" data-id="element-3921" data-mp="3gcixxgcdcbhl">No {category.name.toLowerCase()} linked yet.</p>}
    </>;
}
function TagLegend({
  tag
}: {
  tag?: ActivityTag;
}) {
  if (!tag) return null;
  return <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] font-medium text-app-muted" data-id="element-3922" data-mp="1lz9vg8i8pq8u">
      <span className="size-1.5 rounded-full" style={{
      backgroundColor: TAG_DOT[tag]
    }} data-id="element-3923" data-mp="1h1htsmrsmprg" />
      {tag}
    </span>;
}